import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { getContractor } from '../lib/api';
import { formatDate, getReputationBg } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { CounterpartyProfile } from '../components/adam/CounterpartyProfile';
import { ArrowLeft, Mail, Phone, Building, MessageSquare } from 'lucide-react';

export default function ContractorDetail({ lang }) {
  const { id } = useParams();
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadContractor(); }, [id]);

  async function loadContractor() {
    setLoading(true);
    try {
      const data = await getContractor(id);
      setContractor(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full" />)}</div>;
  }

  if (!contractor) return <p>Not found</p>;

  return (
    <div className="space-y-6" data-testid="contractor-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/contractors')} data-testid="back-button">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {contractor.name}
          </h1>
          {contractor.company && (
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Building className="w-3 h-3" /> {contractor.company}
            </p>
          )}
        </div>
        <Badge variant="outline" className={`ml-auto text-sm ${getReputationBg(contractor.reputation)}`}>
          {t('reputation', lang)}: {Math.round(contractor.reputation * 100)}%
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Contact Info */}
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">{lang === 'pl' ? 'Kontakt' : 'Contact'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {contractor.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                {contractor.email}
              </div>
            )}
            {contractor.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                {contractor.phone}
              </div>
            )}
            {contractor.notes && (
              <div className="mt-3 p-3 rounded-lg bg-muted/30 text-sm text-muted-foreground">
                {contractor.notes}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Psychological Profile */}
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">{t('psychProfile', lang)}</CardTitle>
          </CardHeader>
          <CardContent>
            <CounterpartyProfile profile={contractor.profile} lang={lang} />
          </CardContent>
        </Card>

        {/* Negotiation History */}
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              {lang === 'pl' ? 'Historia negocjacji' : 'Negotiation History'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!contractor.negotiations || contractor.negotiations.length === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-6">{t('noData', lang)}</p>
            ) : (
              <div className="space-y-2">
                {contractor.negotiations.map(neg => (
                  <div
                    key={neg.id}
                    className="p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/negotiations/${neg.id}`)}
                  >
                    <p className="text-sm font-medium truncate">{neg.title}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(neg.created_at)}</p>
                    <Badge variant="secondary" className="text-xs mt-1">{neg.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Experience Events Timeline */}
      {contractor.events && contractor.events.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">{lang === 'pl' ? 'Historia zdarze\u0144' : 'Event History'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {contractor.events.slice(0, 20).map((evt, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    evt.outcome === 'sukces' || evt.outcome === 'dobra_wspolpraca' ? 'bg-emerald-400' :
                    evt.outcome === 'oszukal' || evt.outcome === 'nie_dotrzymal' ? 'bg-red-400' :
                    'bg-amber-400'
                  }`} />
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium">{evt.type}</span>
                    {evt.notes && <span className="text-xs text-muted-foreground ml-2">{evt.notes}</span>}
                  </div>
                  <span className="text-xs text-muted-foreground font-mono flex-shrink-0">
                    {formatDate(evt.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
